import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, CircularProgress, Button, Snackbar, Alert, Chip,
  Skeleton, LinearProgress 
} from '@mui/material';
import testService from '../services/testService';
import { useNavigate } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import 'chart.js/auto';

const MyResults = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [percentiles, setPercentiles] = useState([]);

  const getSection = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('verbal')) return 'Verbal';
    if (n.includes('math')) return 'Math';
    return 'Unknown';
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const resp = await testService.getMyResults();
        
        let questionsList;
        let resultsList;
        if (Array.isArray(resp)) {
          
          resultsList = resp;
          
          const qmap = new Map();
          resp.forEach(r => r.details.forEach(d => qmap.set(d.questionNumber, d.questionType)));
          questionsList = Array.from(qmap.entries()).map(([number, type]) => ({ number, type }));
        } else {
          questionsList = resp.questions ?? resp.Questions ?? [];
          resultsList = resp.results ?? resp.Results ?? [];
        }
        setQuestions(questionsList);
        
        const normalizedResults = resultsList.map(r => {
          const testName = r.testName ?? r.TestName;
          const section = getSection(testName);
          return {
            id: r.testId ?? r.TestId,
            testName,
            correctCount: r.correctCount ?? r.CorrectCount,
            incorrectCount: r.incorrectCount ?? r.IncorrectCount,
            score: r.score ?? r.Score,
            completedAt: r.completedAt ?? r.CompletedAt,
            analysisUrl: r.analysisUrl ?? r.AnalysisUrl ?? '',
            section,
          };
        });
        // Sort by section (Verbal -> Math -> Unknown), then by completedAt desc
        const order = (s) => (s === 'Verbal' ? 0 : s === 'Math' ? 1 : 2);
        normalizedResults.sort((a, b) => {
          const so = order(a.section) - order(b.section);
          if (so !== 0) return so;
          return new Date(b.completedAt) - new Date(a.completedAt);
        });
        setResults(normalizedResults);

        // Fetch percentile comparison
        try {
          const p = await testService.getMyPercentiles();
          const plist = p.results ?? p.Results ?? [];
          setPercentiles(plist);
        } catch (_) {
          setPercentiles([]);
        }
      } catch (err) {
        setError('Failed to load past results');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  // Chart data: Correct answers over time, split by section
  const correctTrendData = useMemo(() => {
    if (!results?.length) return null;
    const sorted = [...results].sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));
    const labels = sorted.map(r => new Date(r.completedAt).toLocaleDateString());
    const verbalData = sorted.map(r => r.section === 'Verbal' ? r.correctCount : null);
    const mathData = sorted.map(r => r.section === 'Math' ? r.correctCount : null);
    return {
      labels,
      datasets: [
        {
          label: 'Verbal (Correct)',
          data: verbalData,
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25,118,210,0.2)',
          tension: 0.2,
          spanGaps: true,
          fill: false,
        },
        {
          label: 'Math (Correct)',
          data: mathData,
          borderColor: '#2e7d32',
          backgroundColor: 'rgba(76,175,80,0.2)',
          tension: 0.2,
          spanGaps: true,
          fill: false,
        }
      ]
    };
  }, [results]);

  // Percentile chart: How the student performed vs peers per test
  const percentileBarData = useMemo(() => {
    if (!percentiles?.length) return null;
    // Keep latest 10 items for readability
    const sorted = [...percentiles]
      .sort((a, b) => new Date(b.completedAt ?? b.CompletedAt) - new Date(a.completedAt ?? a.CompletedAt))
      .slice(0, 10)
      .reverse(); // oldest to latest for y-axis
    const labels = sorted.map(p => p.TestName ?? p.testName ?? `Test ${p.TestId ?? p.testId}`);
    const data = sorted.map(p => p.Percentile ?? p.percentile ?? 0);
    return {
      labels,
      datasets: [
        {
          label: 'Percentile (%)',
          data,
          backgroundColor: 'rgba(25,118,210,0.6)'
        }
      ]
    };
  }, [percentiles]);

  


  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Skeleton variant="rectangular" height={300} />
          <Skeleton variant="rectangular" height={300} />
        </Box>
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!results.length) {
    return <Typography>No past results found</Typography>;
  }

  return (
    <>
    {/* Charts */}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Correct Answers Trend</Typography>
        {correctTrendData ? (
          <Line data={correctTrendData} options={{ responsive: true, plugins: { legend: { display: true } }, scales: { y: { beginAtZero: true } } }} />
        ) : (
          <Typography variant="body2" color="textSecondary">Not enough data</Typography>
        )}
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>How You Compare</Typography>
        {percentileBarData ? (
          <Bar 
            data={percentileBarData} 
            options={{ 
              responsive: true, 
              indexAxis: 'y', 
              plugins: { 
                legend: { display: false }, 
                tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.x}% of students` } }
              },
              scales: { x: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } } }
            }} 
          />
        ) : (
          <Typography variant="body2" color="textSecondary">Percentile data not available yet</Typography>
        )}
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          You performed better than N% of students on each test (based on correct answers).
        </Typography>
      </Paper>
    </Box>

    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Test</TableCell>
            <TableCell>Section</TableCell>
            <TableCell>Correct</TableCell>
            <TableCell>Incorrect</TableCell>
            <TableCell>Completed At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r, idx) => (
            <TableRow key={idx}>
              <TableCell>{r.testName}</TableCell>
              <TableCell>
                <Chip size="small" label={r.section} color={r.section === 'Verbal' ? 'info' : r.section === 'Math' ? 'success' : 'default'} />
              </TableCell>
              <TableCell>{r.correctCount}</TableCell>
              <TableCell>{r.incorrectCount}</TableCell>
              <TableCell>{new Date(r.completedAt).toLocaleString()}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={() => navigate(`/detailed-results/${r.id}`)}>Detailed Results</Button>
                  <Button variant="outlined" size="small" onClick={() => navigate(`/leaderboard/${r.id}`)}>Leaderboard</Button>
                  <Button variant="outlined" size="small" onClick={() => {
  if (r.analysisUrl) {
    window.open(r.analysisUrl, '_blank');
  } else {
    setSnackbarMessage('Analysis is not available yet');
    setSnackbarOpen(true);
  }
}}>Watch test analysis</Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default MyResults;
